import React, { useEffect, useRef, useState } from "react";
import { FaUser, FaBuilding, FaHashtag } from "react-icons/fa";
import { FaArrowLeft } from "react-icons/fa";
import { FaPlus } from "react-icons/fa";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// import DocumentTab from "./Tabs/DocumentTab";

/**
 * TenantCreation
 * -----------------------------------------------------------------------
 * Same shell / interaction pattern as CustomerCreation.jsx (header bar,
 * account-prefix + seq creation, dept code, document accordion, save flow,
 * snackbar) but the form fields are trimmed down to only what exists in
 * TenantRegister.jsx's TenantFormView:
 *   General info        -> tenantId, seq, name, contact, salesman
 *   Address info         -> postalCode, country, address1, address2, areaCode
 *   Communication info    -> telephone, mobile, fax, email
 *   Financial info        -> terms, openBalance, pdc, trn
 * -----------------------------------------------------------------------
 */

const TenantCreation = () => {
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";

  const [tenantName, setTenantName] = useState("");
  const [deptCode, setDeptCode] = useState("");
  const [deptDesc, setDeptDesc] = useState("");
  const [accountListTenant, setAccountListTenant] = useState([]);
  const [deptCodeList, setDeptCodeList] = useState([]);
  const [salesmanList, setSalesmanList] = useState([]);
  const [accountListTenantNewNum, setAccountListTenantNewNum] = useState("");
  const [sendAccountNo, setSendAccountNo] = useState("");
  const [sendAccountNoEdit, setSendAccountNoEdit] = useState("");
  const [sendAccountNameEdit, setSendAccountNameEdit] = useState("");
  const [selectedPrefix, setSelectedPrefix] = useState("");
  const [showBalance, setShowBalance] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  // ---- form data, limited to TenantRegister.jsx fields -------------------
  const [formData, setFormData] = useState({
    contact: "",
    salesman: "",
    postalCode: "",
    country: "",
    address1: "",
    address2: "",
    areaCode: "",
    telephone: "",
    mobile: "",
    fax: "",
    email: "",
    terms: "",
    openBalance: "",
    pdc: "",
    trn: "",
  });

  const setField = (key) => (e) =>
    setFormData((f) => ({ ...f, [key]: e.target.value }));

  const docTabRef = useRef();

  // ---- lifecycle -----------------------------------------------------------
  useEffect(() => {
    fetchAccountListTenant();
    fetchDeptCodeList();
    fetchSalesmen();
  }, []);

  useEffect(() => {
    const state = location.state;
    if (state?.editAccNo) {
      const full = state.editAccNo;
      const nameEdit = state.tenantName;
      setSendAccountNoEdit(full);
      setSendAccountNameEdit(nameEdit);
      const prefix = full.slice(0, 4);
      const suffix = full.slice(4, 8);

      setSendAccountNo(full);
      setAccountListTenantNewNum(suffix);
      setSelectedPrefix(prefix);
    }
  }, [location.state]);

  // ---- API calls -------------------------------------------------------
  const fetchAccountListTenant = async () => {
    let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    let department = localStorage.getItem("currentSelectedDepartment") || "-";
    let apiURL = `${apiPaths[0]?.API_CONFIG}/api/CRMAccountList/${COMPANY_CODE}/TYPELIST/${department}/CUSTOMERS/-/-`;

    try {
      const response = await axios.get(apiURL);
      const data = response.data;
      setAccountListTenant(data || []);

      if (location.state?.editAccNo) {
        const prefix = location.state.editAccNo.slice(0, 4);
        const match = data.find((a) => a.glcat === prefix);
        if (match) {
          setSelectedPrefix(match.glcat);
          handleAccountListSelect(match.glcat);
          return;
        }
      }

      if (!location.state?.editAccNo) {
        const first = data[0]?.glcat;
        if (first && !selectedPrefix) {
          setSelectedPrefix(first);
          handleAccountListSelect(first);
        }
      }
    } catch (err) {
      console.error("Error while fetching account list", err);
    }
  };

  const fetchDeptCodeList = async () => {
    let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    let apiURL = `${apiPaths[0]?.API_CONFIG}/api/CpaysCount/${COMPANY_CODE}/DEPTLIST/-/-`;

    try {
      const response = await axios.get(apiURL);
      setDeptCodeList(response.data || []);
    } catch (err) {
      console.error("Error while fetching dept list", err);
    }
  };

  const fetchSalesmen = async () => {
    let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    let department = localStorage.getItem("currentSelectedDepartment") || "-";
    let apiURL = `${apiPaths[0]?.API_CONFIG}/api/MasterCount/${COMPANY_CODE}/SALESPERSONLIST/-/${department}`;

    try {
      const response = await axios.get(apiURL);
      setSalesmanList(response.data || []);
    } catch (err) {
      console.error("Error while fetching salesmen", err);
      setSalesmanList([]);
    }
  };

  const fetchAccountTenantNewNum = async (selectedAccountNo) => {
    let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    let apiURL = `${apiPaths[0]?.API_CONFIG}/api/CRMGLSettings/${COMPANY_CODE}/ACCOUNTNEWNUM/${selectedAccountNo}/-/-/-`;

    try {
      const response = await axios.get(apiURL);
      const data = response.data[0].Column1;
      setSendAccountNo(data);
      setAccountListTenantNewNum(data.slice(4, 8));
    } catch (err) {
      console.error("Error while fetching account new num", err);
    }
  };

  const handleAccountListSelect = (selectedAccountNo) => {
    if (!sendAccountNoEdit) {
      fetchAccountTenantNewNum(selectedAccountNo);
    }
  };

  // ---- payload builder --------------------------------------------------
  const buildPayload = () => ({
    cmpcode: COMPANY_CODE,
    mod: sendAccountNoEdit ? "M" : "I",
    account: sendAccountNoEdit ? sendAccountNoEdit : sendAccountNo || "",
    acctype: selectedPrefix || "",
    accdesc: sendAccountNoEdit ? sendAccountNameEdit : tenantName || "",
    balance_open: Number(formData.openBalance) || 0,
    curbalance_open: 0,
    dr: 0,
    cr: 0,
    fc: "AED",
    fdr: 0,
    fcr: 0,
    divcode: "",
    keywords: "",
    adr1: formData.address2 || "",
    adr2: formData.address1 || "",
    adr3: formData.country || "",
    adr4: "",
    adr5: "",
    tel: formData.telephone || "",
    fax: formData.fax || "",
    mobile: formData.mobile || "",
    salesman: formData.salesman || "",
    areacode: formData.areaCode || "",
    email: formData.email || "",
    remarks: "",
    country: formData.country || "",
    state: "",
    crlimit: 0,
    curcrlimit: 0,
    currency: "AED",
    duedays: 0,
    terms: formData.terms || "",
    crmethod: "",
    cntrlcode: "CUST",
    avtive: "Y",
    colourcode: "",
    price_cat: "",
    grade: formData.postalCode || "",
    vattype: "",
    vatregno: formData.trn || "",
    shippingadr: "",
    create_user: "",
    discount: 0,
    margin: 0,
    GPSCorditate: "",
    PriWhatsapp: "",
    OtWhatsapp: "",
    QtnStat: "",
    InvStat: "",
    RetnStat: "",
    UserTrn: formData.trn || "",
    UseForDel: "",
    custtype: "LOCAL",
    autoid: "1",
    patron: formData.contact || "",
    patrontype: "",
    officenumber: "",
    regno: "",
    dumpfield: "",
  });

  // ---- save handlers ------------------------------------------------------
  const onTenantSaveAll = async () => {
    if (docTabRef.current) {
      docTabRef.current.saveDocuments();
    }
    let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    let apiURL = `${apiPaths[0]?.API_CONFIG}/api/CRMCustSupptCRMReg`;

    try {
      const response = await axios.post(apiURL, [buildPayload()], {
        headers: { "Content-Type": "application/json" },
      });
      const autoId = response.data.autoid;
      setSnackbarMsg("Tenant saved successfully");
      setSnackbarType("success");
      setSnackbarOpen(true);

      if (docTabRef.current) {
        docTabRef.current.saveDocuments(autoId);
      }
    } catch (err) {
      console.error("Error while saving tenant", err);
      setSnackbarMsg(err?.response?.data?.message || "Failed to save tenant");
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  const onTenantSave = async () => {
    if (!sendAccountNoEdit && docTabRef.current) {
      docTabRef?.current?.saveDocDetails();
    }
    let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    let apiURL = `${apiPaths[0]?.API_CONFIG}/api/CRMCustSupptCRMReg`;

    try {
      await axios.post(apiURL, [buildPayload()], {
        headers: { "Content-Type": "application/json" },
      });
      setSnackbarMsg("Tenant edited successfully");
      setSnackbarType("success");
      setSnackbarOpen(true);

    } catch (err) {
      console.error("Error while editing tenant", err);
      setSnackbarMsg(err?.response?.data?.message || "Failed to edit tenant");
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  const onBack = () => navigate("/tenantRegister");
  const onTenantCreation = () => {
    navigate(`/tenantCreation`);
    window.location.reload();
  };

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="flex flex-wrap items-center justify-between mb-4">
        <h2 className="text-2xl font-normal text-slate-900 tracking-tight">
          {sendAccountNoEdit ? "Tenant Edit" : "Tenant Creation"}
        </h2>

 {/* <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Tenant Register</h2> */}
     


        {sendAccountNoEdit ? (
          <div className="flex justify-center items-center gap-4">
            <button
              onClick={onTenantCreation}
              className="relative ml-4 bg-green-600 text-white font-medium text-[17px] px-2 py-[0.35em] h-[1.8em] rounded-[0.9em] flex items-center overflow-hidden cursor-pointer group"
            >
              <span className="mr-8">New</span>
              <div className="absolute right-[0.3em] bg-white h-[1.2em] w-[1.4em] rounded-[0.7em] flex items-center justify-center transition-all duration-300 group-hover:w-[calc(100%-0.6em)] active:scale-95">
                <FaPlus className="text-green-600" />
              </div>
            </button>
            <button
              onClick={onBack}
              className="relative bg-gray-600 text-white font-medium text-[17px] px-2 py-[0.25em] pl-3 h-[1.8em] rounded-[0.9em] flex items-center overflow-hidden cursor-pointer group"
            >
              <span className="mr-8">Back</span>
              <div className="absolute right-[0.3em] bg-white h-[1.4em] w-[1.4em] rounded-[0.7em] flex items-center justify-center transition-all duration-300 group-hover:w-[calc(100%-0.6em)] active:scale-95">
                <FaArrowLeft className="text-gray-600" />
              </div>
            </button>
          </div>
        ) : (
          <button
            onClick={onBack}
            className="relative bg-gray-600 text-white font-medium text-[17px] px-2 py-[0.25em] pl-3 h-[1.8em] rounded-[0.9em] flex items-center overflow-hidden cursor-pointer group"
          >
            <span className="mr-10">Back</span>
            <div className="absolute right-[0.3em] bg-white h-[1.4em] w-[1.4em] rounded-[0.7em] flex items-center justify-center transition-all duration-300 group-hover:w-[calc(100%-0.6em)] active:scale-95">
              <FaArrowLeft className="text-gray-600" />
            </div>
          </button>
        )}
      </div>

      {/* Name / Account / Dept bar */}
      <div className="mb-4 bg-white flex justify-between items-center p-4 rounded-lg shadow-sm">
        <div className="w-[30%]">
          <label className="block text-sm font-medium mb-1 flex items-center">
            <FaUser className="mr-2 text-gray-600" />
            Tenant Name
          </label>

          {sendAccountNoEdit ? (
            <input
              value={sendAccountNameEdit}
              onChange={(e) => setSendAccountNameEdit(e.target.value)}
              className="w-[64%] px-1 py-2 text-sm rounded bg-gray-100 border border-gray-200"
            />
          ) : (
            <input
              placeholder="Enter Tenant Name Here..."
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              className="placeholder-gray-400 w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
            />
          )}
        </div>

        <div className="flex gap-4 items-center justify-center w-[60%]">
          <div className="w-[30%]">
            <label className="block text-sm font-medium mb-1 flex items-center">
              <FaHashtag className="mr-2 text-gray-600" />
              Account
            </label>
            {sendAccountNoEdit ? (
              <input
                readOnly
                value={sendAccountNoEdit}
                className="w-[100px] px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            ) : (
              <div className="flex">
                <select
                  value={selectedPrefix}
                  onChange={(e) => {
                    setSelectedPrefix(e.target.value);
                    handleAccountListSelect(e.target.value);
                  }}
                  className="w-[80px] px-1 py-2 text-sm rounded bg-gray-100 border border-gray-200"
                >
                  {accountListTenant.map((acc) => (
                    <option key={acc.glcat} value={acc.glcat}>
                      {acc.glcat}
                    </option>
                  ))}
                </select>

                <input
                  readOnly
                  value={accountListTenantNewNum}
                  className="w-[80px] px-1 py-2 text-sm rounded bg-gray-100 border border-gray-200"
                />
              </div>
            )}
          </div>
          <div className="w-full">
            <label className="block text-sm font-medium mb-1 flex items-center">
              <FaBuilding className="mr-2 text-gray-600" /> Dept Code
            </label>
            <select
              onChange={(e) => {
                const selectedDept = deptCodeList.find(
                  (dept) => dept.deptno === e.target.value
                );
                setDeptCode(e.target.value);
                setDeptDesc(selectedDept ? selectedDept.deptnodesc : "");
              }}
              className="px-1 py-2 text-sm rounded bg-gray-100 border border-gray-200"
            >
              <option className="italic text-gray-300" value="all">
                ALL
              </option>
              {deptCodeList.map((dept, index) => (
                <option key={index} value={dept.deptno}>
                  {dept.deptnodesc}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Form sections — fields limited to TenantRegister.jsx's field set */}
      <div className="mt-4 space-y-6">
        {/* General information */}
        <div className="bg-white shadow-sm rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            General Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contact person</label>
              <input
                value={formData.contact}
                onChange={setField("contact")}
                placeholder="Contact name"
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Salesman</label>
              <select
                value={formData.salesman}
                onChange={setField("salesman")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              >
                <option value="">Select salesman</option>
                {salesmanList.map((s, index) => (
                  <option key={index} value={s.NAME || s.EMP_ID || ""}>
                    {s.NAME || ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Address information */}
        <div className="bg-white shadow-sm rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Address Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Postal code</label>
              <input
                value={formData.postalCode}
                onChange={setField("postalCode")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Country</label>
              <select
                value={formData.country}
                onChange={setField("country")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              >
                <option value="">Select country</option>
                <option value="ae">United Arab Emirates</option>
                <option value="in">India</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Area code</label>
              <select
                value={formData.areaCode}
                onChange={setField("areaCode")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              >
                <option value="">Select area</option>
                <option value="al-nahda">Al Nahda</option>
                <option value="deira">Deira</option>
              </select>
            </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
            <div className="">
              <label className="block text-sm font-medium mb-1">Address line 1</label>
              <input
                value={formData.address1}
                onChange={setField("address1")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            <div className="">
              <label className="block text-sm font-medium mb-1">Address line 2</label>
              <input
                value={formData.address2}
                onChange={setField("address2")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            </div>
            </div>

            
        </div>

        {/* Communication information */}
        <div className="bg-white shadow-sm rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Communication Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Telephone</label>
              <input
                value={formData.telephone}
                onChange={setField("telephone")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Mobile</label>
              <input
                value={formData.mobile}
                onChange={setField("mobile")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fax</label>
              <input
                value={formData.fax}
                onChange={setField("fax")}
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={setField("email")}
                placeholder="name@company.com"
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
          </div>
        </div>

        {/* Financial information */}
        <div className="bg-white shadow-sm rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Financial Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Terms</label>
              <input
                value={formData.terms}
                onChange={setField("terms")}
                placeholder="e.g. Net 30"
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Opening balance</label>
              <div className="flex gap-2">
                <input
                  type={showBalance ? "text" : "password"}
                  value={formData.openBalance}
                  onChange={setField("openBalance")}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => setShowBalance((s) => !s)}
                  className="shrink-0 px-3 text-xs rounded bg-gray-100 border border-gray-200 text-gray-600"
                >
                  {showBalance ? "Hide" : "View"}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">PDC</label>
              <input
                disabled
                value={formData.pdc}
                placeholder="No cheques on file"
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200 text-gray-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">TRN</label>
              <input
                value={formData.trn}
                onChange={setField("trn")}
                placeholder="100XXXXXXXXXXXX"
                className="w-full px-3 py-2 text-sm rounded bg-gray-100 border border-gray-200 font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {sendAccountNoEdit && (
        <div className="flex justify-center items-center mt-8">
          <button
            onClick={onTenantSave}
            className="px-3 py-2 text-white text-sm border rounded w-[270px] transition bg-gray-700 hover:bg-black"
          >
            Save Personal &amp; Financial
          </button>
        </div>
      )}



      {!sendAccountNoEdit && (
        <div className="flex justify-center items-center mt-8">
          <button
            onClick={onTenantSaveAll}
            className="px-3 py-2 text-sm border rounded w-[270px] transition bg-gray-700 text-white hover:bg-black"
          >
            Save All
          </button>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbarType}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default TenantCreation;